// This wire format is also used by humanteq-landing. It contains no quiz answers.
export const CAMPAIGN_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'yclid'] as const
type Entry = 'landing' | 'external_quiz' | 'pwa_direct'
interface Attribution {
  entry_point: Entry
  campaign: Partial<Record<typeof CAMPAIGN_KEYS[number], string>>
  quiz_completed: boolean
  pwa_opened: boolean
  updated: number
}
const KEY = 'breakup_attribution_v1'
const VISIT_MS = 30 * 60 * 1000
let fallback: Attribution | undefined

function restore(value: string | null | undefined): Attribution | undefined {
  try {
    const raw = JSON.parse(value || '')
    if (!['landing', 'external_quiz', 'pwa_direct'].includes(raw.entry_point) || !Number.isFinite(raw.updated) || Date.now() - raw.updated >= VISIT_MS) return
    const campaign: Attribution['campaign'] = {}
    for (const key of CAMPAIGN_KEYS) if (typeof raw.campaign?.[key] === 'string') campaign[key] = raw.campaign[key].slice(0, 500)
    return { entry_point: raw.entry_point, campaign, updated: raw.updated, quiz_completed: raw.quiz_completed === true, pwa_opened: raw.pwa_opened === true }
  } catch { return }
}

export function readAttribution(entry: Entry = 'pwa_direct'): Attribution {
  let stored: Attribution | undefined
  let unavailable = false
  try {
    const cookie = document.cookie.split('; ').find(c => c.startsWith(`${KEY}=`))?.slice(KEY.length + 1)
    stored = restore(cookie && decodeURIComponent(cookie)) || restore(localStorage.getItem(KEY))
  } catch { unavailable = true }
  const result = stored || (unavailable ? restore(JSON.stringify(fallback)) : undefined) || { entry_point: entry, campaign: {}, quiz_completed: false, pwa_opened: false, updated: Date.now() }
  return result
}

export function saveAttribution(value: Attribution): void {
  value.updated = Date.now()
  fallback = value
  const encoded = JSON.stringify(value)
  try { localStorage.setItem(KEY, encoded) } catch { /* in-memory fallback */ }
  try {
    const domain = location.hostname === 'humanteq.com' || location.hostname.endsWith('.humanteq.com') ? '; Domain=humanteq.com' : ''
    document.cookie = `${KEY}=${encodeURIComponent(encoded)}; Max-Age=1800; Path=/${domain}; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`
  } catch { /* first-party storage fallback */ }
}

export function captureAttribution(entry: Entry = 'pwa_direct'): Attribution {
  const value = readAttribution(entry)
  const query = new URLSearchParams(location.search)
  for (const key of CAMPAIGN_KEYS) {
    const param = query.get(key)
    if (param) value.campaign[key] = param.slice(0, 500)
  }
  saveAttribution(value)
  return value
}

export function attributedUrl(path: string): string {
  const target = new URL(path, location.origin)
  target.search = ''
  target.hash = ''
  for (const [key, value] of Object.entries(readAttribution().campaign)) target.searchParams.set(key, value)
  return target.href
}

export function addCampaignToLink(target: URL): URL {
  for (const [key, value] of Object.entries(readAttribution().campaign)) target.searchParams.set(key, value)
  return target
}
